import { useEffect } from 'react';
import { Link, useParams, useSearch, useNavigate } from '@tanstack/react-router';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@rudi/ui/tabs';
import { TeamDetailsEditor } from '@rudi/ui/team-details-editor';
import { toast } from 'sonner';
import {
  useArchiveTeam,
  useTenant,
  useTenantTeams,
  useUnarchiveTeam,
  useUpdateTeam,
} from '../api/hooks';
import type { AdminTeam } from '../api/types';
import { PageHeader, QueryState } from '../components/page';
import { TeamMembersPanel } from './TeamMembersPanel';
import { TeamClinicalSetupPanel } from './TeamClinicalSetupPanel';
import { TeamChatDefaultsPanel } from './TeamChatDefaultsPanel';
import {
  TeamWorkspacePanel,
  useTeamWorkspace,
  WorkspaceUnavailable,
  type TeamTab,
  type TeamWorkspaceSearch,
} from './TeamWorkspacePanel';

function TeamActions({ tenantId, team }: { tenantId: string; team: AdminTeam }) {
  const update = useUpdateTeam(tenantId);
  const archive = useArchiveTeam(tenantId);
  const restore = useUnarchiveTeam(tenantId);
  const occupied = team.activeMemberCount + (team.preassignedMemberCount ?? 0) > 0;
  const active = team.status === 'active';
  const changeStatus = async () => {
    try {
      await (active ? archive : restore).mutateAsync(team.id);
      toast.success(active ? 'Team archived' : 'Team restored');
    } catch (error) {
      const data = (error as { response?: { data?: { detail?: string; error?: string } } }).response
        ?.data;
      toast.error(data?.detail ?? data?.error ?? 'Could not update team status');
    }
  };

  return (
    <>
      <TeamDetailsEditor
        team={team}
        onSave={data => update.mutateAsync({ teamId: team.id, ...data })}
      />
      <Button
        variant="outline"
        size="sm"
        disabled={archive.isPending || restore.isPending || (active && occupied)}
        title={
          active && occupied
            ? 'Remove all registered and preassigned members before archiving'
            : undefined
        }
        onClick={() => void changeStatus()}
      >
        {active ? 'Archive' : 'Restore'}
      </Button>
    </>
  );
}

export function TeamDetailPage() {
  const { tenantId, teamId } = useParams({ from: '/tenants/$tenantId/teams/$teamId' });
  const search = useSearch({ from: '/tenants/$tenantId/teams/$teamId' });
  const navigate = useNavigate({ from: '/tenants/$tenantId/teams/$teamId' });
  const workspace = useTeamWorkspace(tenantId, teamId);
  const select = (update: Partial<TeamWorkspaceSearch>) => {
    void navigate({ search: previous => ({ ...previous, ...update }) });
  };
  useEffect(() => {
    if (workspace.data?.investigators.length === 1 && !search.pi) {
      const pi = workspace.data.investigators[0].slug;
      void navigate({ search: previous => ({ ...previous, pi }), replace: true });
    }
  }, [navigate, search.pi, workspace.data]);
  const tenant = useTenant(tenantId);
  const teams = useTenantTeams(tenantId);
  const team = teams.data?.find(item => item.id === teamId);

  return (
    <div>
      <nav
        aria-label="Breadcrumb"
        className="mb-4 flex flex-wrap items-center gap-2 text-sm text-muted-foreground"
      >
        <Link
          to="/tenants/$tenantId"
          params={{ tenantId }}
          activeOptions={{ exact: true }}
          className="hover:underline"
        >
          {tenant.data?.name ?? 'Tenant'}
        </Link>
        <span aria-hidden="true">/</span>
        <Link
          to="/tenants/$tenantId/teams"
          params={{ tenantId }}
          activeOptions={{ exact: true }}
          className="hover:underline"
        >
          Teams
        </Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{team?.displayName ?? 'Team'}</span>
      </nav>
      <QueryState isLoading={teams.isLoading} error={teams.error}>
        {team ? (
          <>
            <PageHeader
              title={team.displayName}
              description={`Manage this team's clinical workspace in ${tenant.data?.name ?? 'this tenant'}.`}
              actions={<TeamActions key={team.id} tenantId={tenantId} team={team} />}
            />
            <div className="mb-6 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <Badge variant={team.status === 'active' ? 'default' : 'outline'}>
                {team.status}
              </Badge>
              <span>{team.activeMemberCount} registered</span>
              <span>{team.preassignedMemberCount ?? 0} before signup</span>
              <span>{team.slug}</span>
            </div>
            <Tabs
              key={team.id}
              value={search.tab ?? 'overview'}
              onValueChange={tab => select({ tab: tab as TeamTab })}
            >
              <TabsList aria-label="Team views" className="mb-4 h-auto flex-wrap">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="studies">Studies</TabsTrigger>
                <TabsTrigger value="profile">Website profile</TabsTrigger>
                <TabsTrigger value="posters">Posters</TabsTrigger>
                <TabsTrigger value="marketing">Digital marketing</TabsTrigger>
                <TabsTrigger value="members">Members</TabsTrigger>
                <TabsTrigger value="practices">Outpatient practices</TabsTrigger>
                <TabsTrigger value="chat">Chat defaults</TabsTrigger>
              </TabsList>
              {!['members', 'practices', 'chat'].includes(search.tab ?? 'overview') && (
                <>
                  {workspace.isLoading ? (
                    <p>Loading team workspace…</p>
                  ) : workspace.error ? (
                    <WorkspaceUnavailable
                      error={workspace.error}
                      retry={() => void workspace.refetch()}
                    />
                  ) : (
                    workspace.data && (
                      <TeamWorkspacePanel
                        key={team.id}
                        tenantId={tenantId}
                        teamId={team.id}
                        data={workspace.data}
                        search={search}
                        navigate={select}
                      />
                    )
                  )}
                </>
              )}
              <TabsContent value="members">
                <TeamMembersPanel tenantId={tenantId} team={team} />
              </TabsContent>
              <TabsContent value="practices">
                <TeamClinicalSetupPanel tenantId={tenantId} team={team} practicesOnly />
              </TabsContent>
              <TabsContent value="chat">
                <TeamChatDefaultsPanel tenantId={tenantId} teamId={team.id} />
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <p role="alert">
            This team was not found in this tenant. Select a team from the sidebar or return to
            Teams.
          </p>
        )}
      </QueryState>
    </div>
  );
}
